import { useState, type ReactNode } from "react";

/**
 * Image that renders `fallback` instead of the browser's broken-image glyph
 * when the src is missing OR fails to load (dead/expired URL). Used anywhere
 * a stored brand-logo URL may have rotted.
 *
 * Error state is keyed to the current src, so a changed src automatically
 * retries without needing a `key` at the call site.
 */
export function ImgWithFallback({
  src,
  alt = "",
  className,
  imgWrapperClassName,
  fallback,
  loading,
  "data-testid": testId,
}: {
  src?: string | null;
  alt?: string;
  className?: string;
  /** Optional wrapper div rendered around the img only (not the fallback). */
  imgWrapperClassName?: string;
  fallback: ReactNode;
  loading?: "lazy" | "eager";
  "data-testid"?: string;
}) {
  const [errorSrc, setErrorSrc] = useState<string | null>(null);
  if (!src || errorSrc === src) {
    return <>{fallback}</>;
  }
  const img = (
    <img
      src={src}
      alt={alt}
      loading={loading}
      className={className}
      data-testid={testId}
      onError={() => setErrorSrc(src)}
    />
  );
  return imgWrapperClassName ? (
    <div className={imgWrapperClassName}>{img}</div>
  ) : (
    img
  );
}
